import * as React from 'react';

import { ComboBox, useComboBoxOptions } from '@rematter/ui-web';

import { useFreightLanesListQuery } from '../../hooks/queries/useFreightLanesListQuery';

import { FreightLaneRecord } from '~/modules/transportation/types';

interface FreightLaneSelectProps {
  label: string;
  value: string | null;
  onChange: (lane: { id: string } | null) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
}

export const FreightLaneSelect = ({
  label,
  value,
  onChange,
  isRequired,
  isDisabled,
}: FreightLaneSelectProps) => {
  const { data } = useFreightLanesListQuery({
    filter: {},
    pagination: { offset: 0, limit: 100 },
  });

  const lanes = React.useMemo(
    () => data?.freightLanes ?? [],
    [data?.freightLanes],
  );

  const laneOptions = React.useMemo(
    () =>
      lanes.map((lane: FreightLaneRecord) => ({
        id: lane.id,
        label:
          lane.destinationCity && lane.destinationState
            ? `${lane.carrierName}: ${lane.destinationCity}, ${lane.destinationState}`
            : lane.carrierName,
      })),
    [lanes],
  );

  const { query, onChangeQuery, filteredOptions } = useComboBoxOptions({
    value: value || '',
    options: laneOptions,
    disabled: isDisabled,
  });

  const handleSelectionChange = (values: string[]) => {
    const lane = lanes.find((l: FreightLaneRecord) => l.id === values[0]);
    onChange(lane ? { id: lane.id } : null);
  };

  return (
    <ComboBox
      label={label}
      value={value ? [value] : []}
      isRequired={isRequired}
      isDisabled={isDisabled}
      onChange={handleSelectionChange}
      query={query}
      onChangeQuery={onChangeQuery}
      placeholder="Select a freight lane"
      className="@FreightLaneSelect"
      options={filteredOptions}
      getSelection={(ids) =>
        ids.map((id) => {
          const lane = lanes.find((l: FreightLaneRecord) => l.id === id);
          if (!lane) return { label: '' };
          return {
            label:
              lane.destinationCity && lane.destinationState
                ? `${lane.carrierName}: ${lane.destinationCity}, ${lane.destinationState}`
                : lane.carrierName,
          };
        })
      }
    />
  );
};
